import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';
import serviceManagementService from '../../../services/serviceManagementService';

const CountryAvailabilityModal = ({ service, countries, onClose, onSave, language }) => {
  const [availability, setAvailability] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // Initialize availability state from service data
    const initialAvailability = {};
    service?.availableCountries?.forEach(ac => {
      initialAvailability[ac?.countryId] = {
        isAvailable: ac?.isAvailable,
        customPrice: ac?.customPrice,
        complianceStatus: ac?.complianceStatus || 'compliant'
      };
    });
    setAvailability(initialAvailability);
  }, [service]);

  const handleToggleCountry = (countryId) => {
    setAvailability(prev => ({
      ...prev,
      [countryId]: {
        isAvailable: !prev?.[countryId]?.isAvailable,
        customPrice: prev?.[countryId]?.customPrice || null,
        complianceStatus: prev?.[countryId]?.complianceStatus || 'compliant'
      }
    }));
  };

  const handleCustomPriceChange = (countryId, price) => {
    setAvailability(prev => ({
      ...prev,
      [countryId]: {
        ...prev?.[countryId],
        customPrice: price ? parseFloat(price) : null
      }
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);

    try {
      const updates = Object.entries(availability)?.map(([countryId, data]) => ({
        countryId,
        isAvailable: data?.isAvailable || false,
        customPrice: data?.customPrice || null,
        complianceStatus: data?.complianceStatus || 'compliant'
      }));

      await serviceManagementService?.bulkUpdateServiceAvailability(service?.id, updates);
      onSave();
      onClose();
    } catch (error) {
      console.error('Error updating availability:', error);
      alert(language === 'FR' ? 'Erreur lors de la mise à jour' : 'Error updating availability');
    } finally {
      setLoading(false);
    }
  };

  const filteredCountries = countries?.filter(country => 
    country?.countryName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    country?.countryCode?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  const availableCount = Object.values(availability)?.filter(a => a?.isAvailable)?.length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
      <div className="bg-card border border-border rounded-lg shadow-modal w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">
              Disponibilité par Pays
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {service?.serviceName} - {availableCount} / {countries?.length} pays
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 border-b border-border">
          <div className="relative">
            <Icon 
              name="Search" 
              size={20} 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" 
            />
            <input
              type="text"
              placeholder="Rechercher un pays..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-3">
            {filteredCountries?.map((country) => {
              const isAvailable = availability?.[country?.id]?.isAvailable || false;
              const customPrice = availability?.[country?.id]?.customPrice || '';

              return (
                <div
                  key={country?.id}
                  className="bg-background border border-border rounded-lg p-4 hover:border-primary/50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <Checkbox
                        checked={isAvailable}
                        onCheckedChange={() => handleToggleCountry(country?.id)}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-foreground">
                            {country?.countryName}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            ({country?.countryCode})
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Devise: {country?.currencyCode}
                        </p>
                      </div>
                    </div>

                    {isAvailable && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">Prix personnalisé:</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={customPrice}
                          onChange={(e) => handleCustomPriceChange(country?.id, e?.target?.value)}
                          placeholder={`${service?.unitPrice} (défaut)`}
                          className="w-32 px-3 py-1 bg-background border border-border rounded text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                        <span className="text-sm text-muted-foreground">{country?.currencyCode}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </form>

        <div className="flex items-center gap-3 p-6 border-t border-border">
          <Button type="submit" fullWidth loading={loading} onClick={handleSubmit}>
            Enregistrer la Disponibilité
          </Button>
          <Button type="button" variant="outline" fullWidth onClick={onClose}>
            Annuler
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CountryAvailabilityModal;